<template>
  <v-container fluid class="message-center-container">
    <v-row>
      <v-col cols="12">
        <!-- Header mit Statistiken -->
        <v-card class="header-card mb-4">
          <v-card-title class="header-title">
            <div class="d-flex align-center">
              <v-icon color="primary" :size="isMobile ? 24 : 32" class="mr-2">mdi-email</v-icon>
              <span class="title-text">Nachrichten-Center</span>
            </div>

            <!-- Statistik-Chips und Buttons in einer Zeile -->
            <div class="header-right">
              <!-- Statistik-Chips -->
              <div class="stats-chips" v-if="!isMobile">
                <v-chip
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-email-outline"
                >
                  Gesamt: {{ stats.received.total }}
                </v-chip>

                <v-chip
                  v-if="stats.received.unread > 0"
                  color="error"
                  variant="elevated"
                  prepend-icon="mdi-email-alert"
                >
                  Ungelesen: {{ stats.received.unread }}
                </v-chip>

                <v-chip
                  v-if="stats.received.urgent_unread > 0"
                  color="warning"
                  variant="elevated"
                  prepend-icon="mdi-alert"
                  class="pulse-chip"
                >
                  Dringend: {{ stats.received.urgent_unread }}
                </v-chip>
              </div>

              <!-- Action Buttons -->
              <div class="header-actions">
                <!-- Neue Nachricht Button -->
                <v-btn
                  color="success"
                  :prepend-icon="isMobile ? undefined : 'mdi-plus'"
                  :icon="isMobile ? 'mdi-plus' : undefined"
                  :size="isMobile ? 'small' : 'default'"
                  @click="showNewMessageDialog = true"
                >
                  <span v-if="!isMobile">Neue Nachricht</span>
                </v-btn>

                <!-- Alles gelesen Button (nur bei Posteingang mit ungelesenen) -->
                <v-btn
                  v-if="activeTab === 'inbox' && stats.received.unread > 0"
                  :icon="isMobile ? 'mdi-email-check' : undefined"
                  :prepend-icon="isMobile ? undefined : 'mdi-email-check'"
                  :size="isMobile ? 'small' : 'default'"
                  color="primary"
                  variant="outlined"
                  @click="markAllAsRead"
                  :loading="loading"
                >
                  <span v-if="!isMobile">Alles gelesen</span>
                </v-btn>

                <!-- Alles archivieren Button (nur bei Posteingang) -->
                <v-btn
                  v-if="activeTab === 'inbox' && messages.length > 0"
                  :icon="isMobile ? 'mdi-archive-arrow-down' : undefined"
                  :prepend-icon="isMobile ? undefined : 'mdi-archive-arrow-down'"
                  :size="isMobile ? 'small' : 'default'"
                  color="warning"
                  variant="outlined"
                  @click="archiveAll"
                  :loading="loading"
                >
                  <span v-if="!isMobile">Alles archivieren</span>
                </v-btn>

                <!-- Aktualisieren Button -->
                <v-btn
                  icon="mdi-refresh"
                  variant="text"
                  :size="isMobile ? 'small' : 'default'"
                  @click="loadMessages"
                  :loading="loading"
                />
              </div>
            </div>
          </v-card-title>

          <!-- Statistik-Chips - nur auf Mobile in zweiter Zeile -->
          <v-card-subtitle v-if="isMobile" class="stats-row pa-3 pt-0">
            <v-chip
              color="primary"
              variant="outlined"
              prepend-icon="mdi-email-outline"
              size="small"
            >
              Gesamt: {{ stats.received.total }}
            </v-chip>

            <v-chip
              v-if="stats.received.unread > 0"
              color="error"
              variant="elevated"
              prepend-icon="mdi-email-alert"
              size="small"
            >
              Ungelesen: {{ stats.received.unread }}
            </v-chip>

            <v-chip
              v-if="stats.received.urgent_unread > 0"
              color="warning"
              variant="elevated"
              prepend-icon="mdi-alert"
              size="small"
              class="pulse-chip"
            >
              Dringend: {{ stats.received.urgent_unread }}
            </v-chip>
          </v-card-subtitle>
        </v-card>

        <!-- Tabs für Posteingang / Gesendet / Archiv -->
        <v-card class="messages-card">
          <v-tabs
            v-model="activeTab"
            color="primary"
            align-tabs="start"
            @update:model-value="loadMessages"
          >
            <v-tab value="inbox">
              <v-icon class="mr-2">mdi-inbox</v-icon>
              Posteingang
              <v-badge
                v-if="stats.received.unread > 0"
                :content="stats.received.unread"
                color="error"
                inline
                class="ml-2"
              />
            </v-tab>
            <v-tab value="sent">
              <v-icon class="mr-2">mdi-send</v-icon>
              Gesendet
            </v-tab>
            <v-tab value="unread">
              <v-icon class="mr-2">mdi-email-alert</v-icon>
              Ungelesen
              <v-badge
                v-if="stats.received.unread > 0"
                :content="stats.received.unread"
                color="error"
                inline
                class="ml-2"
              />
            </v-tab>
            <v-tab value="archived">
              <v-icon class="mr-2">mdi-archive</v-icon>
              Archiv
            </v-tab>
          </v-tabs>

          <v-divider />

          <!-- Nachrichten-Liste -->
          <v-card-text class="pa-0">
            <v-list v-if="messages.length > 0" class="message-list">
              <v-list-item
                v-for="message in messages"
                :key="message.id"
                class="message-item"
                :class="[
                  { 'unread-message': !message.is_read && activeTab !== 'sent' },
                  { 'mobile-message-item': isMobile }
                ]"
                @click="openMessage(message)"
              >
                <template #prepend>
                  <v-avatar
                    :color="getMessageColor(message)"
                    :size="isMobile ? 32 : 40"
                  >
                    <v-icon :size="isMobile ? 18 : 24">{{ getMessageIcon(message) }}</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title class="message-title-row">
                  <span class="font-weight-bold message-subject">{{ message.subject }}</span>

                  <div class="message-chips">
                    <v-chip
                      v-if="message.priority === 'urgent'"
                      size="x-small"
                      color="error"
                      variant="flat"
                    >
                      {{ isMobile ? '!' : 'DRINGEND' }}
                    </v-chip>

                    <v-chip
                      v-if="message.priority === 'high'"
                      size="x-small"
                      color="warning"
                      variant="flat"
                    >
                      {{ isMobile ? '⚠' : 'WICHTIG' }}
                    </v-chip>

                    <v-chip
                      v-if="message.kv_nummer"
                      size="x-small"
                      color="info"
                      variant="outlined"
                    >
                      KV: {{ message.kv_nummer }}
                    </v-chip>
                  </div>
                </v-list-item-title>

                <v-list-item-subtitle class="mt-1">
                  <div class="message-meta">
                    <div class="message-sender">
                      <v-icon :size="isMobile ? 14 : 16" class="mr-1">
                        {{ activeTab === 'sent' ? 'mdi-account-arrow-right' : 'mdi-account' }}
                      </v-icon>
                      <span>
                        {{ activeTab === 'sent'
                          ? `${message.recipient_vorname} ${message.recipient_nachname}`
                          : `${message.sender_vorname} ${message.sender_nachname}`
                        }}
                      </span>
                    </div>
                    <div class="message-time">
                      <v-icon :size="isMobile ? 14 : 16" class="mr-1">mdi-clock-outline</v-icon>
                      <span>{{ formatDate(message.created_at) }}</span>
                    </div>
                  </div>
                  <div class="message-preview mt-1">
                    {{ truncateText(message.message_text, isMobile ? 60 : 100) }}
                  </div>
                </v-list-item-subtitle>

                <template #append>
                  <div :class="isMobile ? 'mobile-message-actions' : 'd-flex flex-column align-end'">
                    <v-icon
                      v-if="!message.is_read && activeTab !== 'sent'"
                      color="primary"
                      :size="isMobile ? 'x-small' : 'small'"
                    >
                      mdi-circle
                    </v-icon>

                    <!-- Bestätigt-Anzeige für KV-Nachrichten -->
                    <v-chip
                      v-if="message.kv_nummer && message.kv_completed"
                      color="success"
                      size="x-small"
                      variant="flat"
                      class="mt-1"
                    >
                      <v-icon size="small">mdi-check-circle</v-icon>
                    </v-chip>

                    <!-- Archivieren-Button (nur nicht im Archiv-Tab) -->
                    <v-btn
                      v-if="activeTab !== 'archived'"
                      icon="mdi-archive"
                      :size="isMobile ? 'x-small' : 'small'"
                      variant="text"
                      color="orange"
                      @click.stop="archiveMessage(message)"
                    />
                  </div>
                </template>
              </v-list-item>
            </v-list>

            <!-- Leerer Zustand -->
            <div v-else class="empty-state pa-8 text-center">
              <v-icon size="80" color="grey-lighten-1">
                {{ activeTab === 'archived' ? 'mdi-archive-off' : 'mdi-email-off-outline' }}
              </v-icon>
              <h3 class="text-h6 mt-4 mb-2">Keine Nachrichten</h3>
              <p class="text-body-2 text-medium-emphasis">
                {{ activeTab === 'inbox' ? 'Ihr Posteingang ist leer' :
                   activeTab === 'sent' ? 'Sie haben noch keine Nachrichten gesendet' :
                   activeTab === 'archived' ? 'Keine archivierten Nachrichten' :
                   'Keine ungelesenen Nachrichten' }}
              </p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Nachricht Detail Dialog -->
    <v-dialog v-model="showMessageDialog" max-width="700">
      <v-card v-if="selectedMessage" class="message-detail-card">
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-avatar :color="getMessageColor(selectedMessage)" size="40" class="mr-3">
              <v-icon>{{ getMessageIcon(selectedMessage) }}</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6">{{ selectedMessage.subject }}</div>
              <div class="text-caption">
                Von: {{ selectedMessage.sender_vorname }} {{ selectedMessage.sender_nachname }}
                • {{ formatDate(selectedMessage.created_at) }}
              </div>
            </div>
          </div>

          <v-btn icon="mdi-close" variant="text" @click="closeMessage" />
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-6">
          <!-- KV Info (falls vorhanden) -->
          <v-alert
            v-if="selectedMessage.kv_nummer"
            :color="getKvStatusColor(selectedMessage.kv_status)"
            variant="tonal"
            class="mb-4"
          >
            <div class="d-flex align-center">
              <v-icon class="mr-2">mdi-file-document</v-icon>
              <div>
                <div class="font-weight-bold">KV-Nummer: {{ selectedMessage.kv_nummer }}</div>
                <div class="text-caption">Status: {{ getKvStatusText(selectedMessage.kv_status) }}</div>
              </div>
            </div>
          </v-alert>

          <!-- Nachrichtentext -->
          <div class="message-content">
            {{ selectedMessage.message_text }}
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <!-- Für KV-Nachrichten: Bestätigen-Button (nur für Monteure) -->
          <v-btn
            v-if="selectedMessage.kv_nummer && selectedMessage.kv_confirmation_id && !selectedMessage.kv_completed"
            color="success"
            variant="elevated"
            prepend-icon="mdi-check-circle"
            @click="openKvCompleteDialog(selectedMessage)"
            :loading="confirming"
          >
            Reparatur bestätigen
          </v-btn>

          <!-- Bestätigt-Anzeige -->
          <v-chip
            v-if="selectedMessage.kv_nummer && selectedMessage.kv_completed"
            color="success"
            prepend-icon="mdi-check-circle"
          >
            Bereits bestätigt
          </v-chip>

          <v-btn
            v-if="!selectedMessage.is_read && activeTab !== 'sent'"
            color="primary"
            variant="text"
            prepend-icon="mdi-email-check"
            @click="markAsRead(selectedMessage)"
          >
            Als gelesen markieren
          </v-btn>

          <!-- Archivieren-Button (nur wenn nicht im Archiv) -->
          <v-btn
            v-if="activeTab !== 'archived'"
            color="orange"
            variant="text"
            prepend-icon="mdi-archive"
            @click="archiveMessage(selectedMessage); closeMessage()"
          >
            Archivieren
          </v-btn>

          <v-btn
            color="grey"
            variant="text"
            @click="closeMessage"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- KV Reparatur Bestätigen Dialog -->
    <v-dialog v-model="showKvCompleteDialog" max-width="600">
      <v-card v-if="selectedKvMessage" class="complete-kv-card">
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="success" size="28" class="mr-3">mdi-check-circle</v-icon>
            <span class="text-h6">Reparatur bestätigen</span>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="closeKvCompleteDialog" />
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-6">
          <v-alert type="info" variant="tonal" class="mb-4">
            <div class="text-caption mb-1">KV-Nummer:</div>
            <div class="font-weight-bold">{{ selectedKvMessage.kv_nummer }}</div>
          </v-alert>

          <!-- Monteur-Notiz -->
          <v-textarea
            v-model="kvCompleteForm.monteur_notiz"
            label="Ihre Notiz (optional)"
            prepend-inner-icon="mdi-note-text"
            variant="outlined"
            rows="3"
            auto-grow
            class="mb-4"
          />

          <!-- In Instandsetzung eintragen -->
          <v-checkbox
            v-model="kvCompleteForm.add_to_instandsetzung"
            label="Als abgeschlossen in Instandsetzung eintragen"
            color="success"
            class="mb-2"
          >
            <template v-slot:append>
              <v-tooltip text="Erstellt automatisch einen Auftrag in Ihren Instandsetzungen" location="top">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props" size="small" color="info">mdi-information</v-icon>
                </template>
              </v-tooltip>
            </template>
          </v-checkbox>

          <v-alert
            v-if="kvCompleteForm.add_to_instandsetzung"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4 text-caption"
          >
            Bitte wählen Sie den Gerätetyp und den Mitarbeiter, der die Instandsetzung durchführen soll.
          </v-alert>

          <!-- Gerätetyp-Auswahl (nur wenn add_to_instandsetzung aktiviert) -->
          <v-select
            v-if="kvCompleteForm.add_to_instandsetzung"
            v-model="kvCompleteForm.geraetetyp_id"
            :items="availableGeraetetypen"
            item-title="name"
            item-value="id"
            label="Gerätetyp auswählen *"
            prepend-inner-icon="mdi-washing-machine"
            variant="outlined"
            :rules="[v => !!v || 'Bitte Gerätetyp auswählen']"
            required
            class="mb-4"
          />

          <!-- Reparaturtyp-Auswahl (nur wenn add_to_instandsetzung aktiviert) -->
          <v-select
            v-if="kvCompleteForm.add_to_instandsetzung"
            v-model="kvCompleteForm.reparaturtyp_id"
            :items="availableReparaturtypen"
            item-title="name"
            item-value="id"
            label="Reparaturtyp auswählen *"
            prepend-inner-icon="mdi-wrench"
            variant="outlined"
            :rules="[v => !!v || 'Bitte Reparaturtyp auswählen']"
            required
            class="mb-4"
          />

          <!-- Mitarbeiter-Auswahl für Instandsetzung (nur wenn add_to_instandsetzung aktiviert) -->
          <v-select
            v-if="kvCompleteForm.add_to_instandsetzung"
            v-model="kvCompleteForm.instandsetzung_mitarbeiter_id"
            :items="availableMonteure"
            item-title="name"
            item-value="id"
            label="Mitarbeiter für Instandsetzung auswählen *"
            prepend-inner-icon="mdi-account"
            variant="outlined"
            :rules="[v => !!v || 'Bitte Mitarbeiter auswählen']"
            required
          >
            <template v-slot:item="{ props, item }">
              <v-list-item
                v-bind="props"
                :subtitle="item.raw.mitarbeiternummer"
              />
            </template>
          </v-select>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="grey"
            variant="text"
            @click="closeKvCompleteDialog"
            :disabled="confirming"
          >
            Abbrechen
          </v-btn>

          <v-btn
            color="success"
            variant="elevated"
            prepend-icon="mdi-check-circle"
            @click="confirmKvCompletion"
            :loading="confirming"
          >
            Bestätigen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Neue Nachricht Dialog -->
    <v-dialog v-model="showNewMessageDialog" max-width="600">
      <v-card class="new-message-card">
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="success" size="28" class="mr-3">mdi-email-edit</v-icon>
            <span class="text-h6">Neue Nachricht</span>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="closeNewMessageDialog" />
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-6">
          <v-form ref="newMessageForm" @submit.prevent="sendMessage">
            <!-- Empfänger -->
            <v-autocomplete
              v-model="newMessage.recipient_id"
              :items="availableUsers"
              item-title="display_name"
              item-value="id"
              label="Empfänger"
              prepend-inner-icon="mdi-account"
              variant="outlined"
              :rules="[v => !!v || 'Empfänger erforderlich']"
              class="mb-4"
            />

            <!-- Priorität -->
            <v-select
              v-model="newMessage.priority"
              :items="priorityOptions"
              label="Priorität"
              prepend-inner-icon="mdi-flag"
              variant="outlined"
              class="mb-4"
            />

            <!-- Betreff -->
            <v-text-field
              v-model="newMessage.subject"
              label="Betreff"
              prepend-inner-icon="mdi-text-subject"
              variant="outlined"
              :rules="[v => !!v || 'Betreff erforderlich']"
              class="mb-4"
            />

            <!-- Nachrichtentext -->
            <v-textarea
              v-model="newMessage.message_text"
              label="Nachricht"
              prepend-inner-icon="mdi-message-text"
              variant="outlined"
              rows="5"
              auto-grow
              :rules="[v => !!v || 'Nachricht erforderlich']"
            />
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="grey"
            variant="text"
            @click="closeNewMessageDialog"
            :disabled="sending"
          >
            Abbrechen
          </v-btn>

          <v-btn
            color="success"
            variant="elevated"
            prepend-icon="mdi-send"
            @click="sendMessage"
            :loading="sending"
          >
            Senden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { useToast } from 'vue-toastification';
import { useAuthStore } from '@/stores/auth';

const toast = useToast();
const authStore = useAuthStore();

// State
const loading = ref(false);
const sending = ref(false);
const confirming = ref(false);
const activeTab = ref('inbox');
const messages = ref([]);
const selectedMessage = ref(null);
const showMessageDialog = ref(false);
const showNewMessageDialog = ref(false);
const showKvCompleteDialog = ref(false);
const selectedKvMessage = ref(null);
const availableUsers = ref([]);
const availableMonteure = ref([]);
const availableGeraetetypen = ref([]);
const availableReparaturtypen = ref([]);

// Responsive Design
const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value < 960);

// Computed
const canDeleteKvMessages = computed(() => {
  return authStore.user?.admin ||
         authStore.user?.super_admin ||
         authStore.user?.developer ||
         false;
});

const stats = ref({
  received: {
    total: 0,
    unread: 0,
    urgent_unread: 0
  },
  sent: {
    total: 0
  }
});

const newMessage = ref({
  recipient_id: null,
  subject: '',
  message_text: '',
  message_type: 'general',
  priority: 'normal'
});

const kvCompleteForm = ref({
  monteur_notiz: '',
  add_to_instandsetzung: false,
  geraetetyp_id: null,
  reparaturtyp_id: null,
  instandsetzung_mitarbeiter_id: null
});

const priorityOptions = [
  { title: 'Niedrig', value: 'low' },
  { title: 'Normal', value: 'normal' },
  { title: 'Hoch', value: 'high' },
  { title: 'Dringend', value: 'urgent' }
];

// Window resize handler
function updateWidth() {
  windowWidth.value = window.innerWidth;
}

// Lifecycle
onMounted(() => {
  loadMessages();
  loadStats();
  loadAvailableUsers();
  loadAvailableMonteure();
  loadAvailableGeraetetypen();
  loadAvailableReparaturtypen();

  // WebSocket-Listener für neue Nachrichten
  window.addEventListener('websocket-message', handleWebSocketMessage);

  // Window resize listener
  window.addEventListener('resize', updateWidth);
});

// Cleanup bei Component Unmount
onUnmounted(() => {
  window.removeEventListener('websocket-message', handleWebSocketMessage);
  window.removeEventListener('resize', updateWidth);
});

// Methods
async function loadMessages() {
  loading.value = true;

  try {
    const action = activeTab.value;
    const response = await axios.get(`${API_BASE_URL}messages.php`, {
      params: { action },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      messages.value = response.data.data || [];
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der Nachrichten');
    }
  } catch (error) {
    console.error('Fehler beim Laden der Nachrichten:', error);
    toast.error('Fehler beim Laden der Nachrichten');
  } finally {
    loading.value = false;
  }
}

async function loadStats() {
  try {
    const response = await axios.get(`${API_BASE_URL}messages.php`, {
      params: { action: 'stats' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      stats.value = response.data.data;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Statistiken:', error);
  }
}

async function loadAvailableUsers() {
  try {
    const response = await axios.get(`${API_BASE_URL}messages.php?action=users`, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      availableUsers.value = response.data.users.map(user => ({
        id: user.id,
        display_name: `${user.vorname} ${user.nachname} (${user.mitarbeiternummer})`,
        ...user
      }));
    }
  } catch (error) {
    console.error('Fehler beim Laden der Benutzer:', error);
  }
}

function openMessage(message) {
  selectedMessage.value = message;
  showMessageDialog.value = true;

  // Als gelesen markieren wenn ungelesen
  if (!message.is_read && activeTab.value !== 'sent') {
    markAsRead(message, false);
  }
}

function closeMessage() {
  showMessageDialog.value = false;
  selectedMessage.value = null;
}

async function markAsRead(message, showToast = true) {
  try {
    const response = await axios.put(
      `${API_BASE_URL}messages.php`,
      {
        action: 'mark_read',
        id: message.id
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      message.is_read = true;
      if (showToast) {
        toast.success('Als gelesen markiert');
      }
      loadStats();
    }
  } catch (error) {
    console.error('Fehler beim Markieren:', error);
    toast.error('Fehler beim Markieren der Nachricht');
  }
}

async function deleteMessage(message) {
  if (!confirm('Möchten Sie diese Nachricht wirklich löschen?')) {
    return;
  }

  try {
    const response = await axios.delete(
      `${API_BASE_URL}messages.php`,
      {
        data: { id: message.id },
        withCredentials: true
      }
    );

    if (response.data.status === 'success') {
      toast.success('Nachricht gelöscht');
      loadMessages();
      loadStats();
    }
  } catch (error) {
    console.error('Fehler beim Löschen:', error);
    toast.error('Fehler beim Löschen der Nachricht');
  }
}

async function archiveMessage(message) {
  try {
    const response = await axios.put(
      `${API_BASE_URL}messages.php`,
      {
        action: 'archive',
        id: message.id
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('Nachricht archiviert');
      // Nachricht aus der aktuellen Liste entfernen
      messages.value = messages.value.filter(m => m.id !== message.id);
      // Statistiken und Listen neu laden
      await loadMessages();
      await loadStats();
    } else {
      toast.error(response.data.message || 'Fehler beim Archivieren');
    }
  } catch (error) {
    console.error('Fehler beim Archivieren:', error);
    toast.error('Fehler beim Archivieren der Nachricht');
  }
}

function openKvCompleteDialog(message) {
  selectedKvMessage.value = message;
  showKvCompleteDialog.value = true;
  // Vorauswahl: Wenn Monteur, wähle sich selbst für Instandsetzung vor
  if (authStore.user) {
    kvCompleteForm.value.instandsetzung_mitarbeiter_id = authStore.user.id;
  }
}

function closeKvCompleteDialog() {
  showKvCompleteDialog.value = false;
  selectedKvMessage.value = null;
  kvCompleteForm.value = {
    monteur_notiz: '',
    add_to_instandsetzung: false,
    geraetetyp_id: null,
    reparaturtyp_id: null,
    instandsetzung_mitarbeiter_id: null
  };
}

async function loadAvailableMonteure() {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/users.php?action=get`, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      availableMonteure.value = response.data.users
        .filter(user => user.monteur)
        .map(user => ({
          id: user.id,
          name: `${user.vorname} ${user.nachname}`,
          mitarbeiternummer: user.mitarbeiternummer
        }));
    }
  } catch (error) {
    console.error('Fehler beim Laden der Monteure:', error);
  }
}

async function loadAvailableGeraetetypen() {
  try {
    const response = await axios.get(`${API_BASE_URL}kv_confirmations.php`, {
      params: { action: 'geraetetypen' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      availableGeraetetypen.value = response.data.data.map(typ => ({
        id: typ.id,
        name: typ.name
      }));
    }
  } catch (error) {
    console.error('Fehler beim Laden der Gerätetypen:', error);
  }
}

async function loadAvailableReparaturtypen() {
  try {
    const response = await axios.get(`${API_BASE_URL}kv_confirmations.php`, {
      params: { action: 'reparaturtypen' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      availableReparaturtypen.value = response.data.data.map(typ => ({
        id: typ.id,
        name: typ.name
      }));
    }
  } catch (error) {
    console.error('Fehler beim Laden der Reparaturtypen:', error);
  }
}

async function confirmKvCompletion() {
  // Validierung: Wenn add_to_instandsetzung aktiviert, müssen Gerätetyp, Reparaturtyp und Mitarbeiter ausgewählt sein
  if (kvCompleteForm.value.add_to_instandsetzung) {
    if (!kvCompleteForm.value.geraetetyp_id) {
      toast.error('Bitte wählen Sie einen Gerätetyp aus');
      return;
    }
    if (!kvCompleteForm.value.reparaturtyp_id) {
      toast.error('Bitte wählen Sie einen Reparaturtyp aus');
      return;
    }
    if (!kvCompleteForm.value.instandsetzung_mitarbeiter_id) {
      toast.error('Bitte wählen Sie einen Mitarbeiter für die Instandsetzung aus');
      return;
    }
  }

  confirming.value = true;

  try {
    // KV als abgeschlossen markieren
    const response = await axios.put(
      `${API_BASE_URL}kv_confirmations.php`,
      {
        action: 'complete',
        id: selectedKvMessage.value.kv_confirmation_id,
        ...kvCompleteForm.value
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('Reparatur bestätigt! KV wurde archiviert.');

      // Nachricht aktualisieren
      if (selectedMessage.value) {
        selectedMessage.value.kv_completed = true;
      }

      // Listen neu laden
      await loadMessages();
      await loadStats();

      // Globalen Counter in App.vue aktualisieren
      window.dispatchEvent(new CustomEvent('update-unread-messages'));

      // Dialoge schließen
      closeKvCompleteDialog();
      closeMessage();
    } else {
      toast.error(response.data.message || 'Fehler beim Bestätigen');
    }
  } catch (error) {
    console.error('Fehler beim Bestätigen der Reparatur:', error);
    console.error('Error Response:', error.response);
    console.error('Error Response Data:', error.response?.data);

    // Zeige Backend-Fehlermeldung an, falls vorhanden
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(`Fehler: ${error.response.data.message}`);
    } else {
      toast.error('Fehler beim Bestätigen der Reparatur');
    }
  } finally {
    confirming.value = false;
  }
}

async function markAllAsRead() {
  if (!confirm('Möchten Sie wirklich alle Nachrichten als gelesen markieren?')) {
    return;
  }

  loading.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}messages.php`,
      { action: 'mark_all_read' },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('Alle Nachrichten wurden als gelesen markiert');
      await loadMessages();
      await loadStats();

      // Globalen Counter aktualisieren
      window.dispatchEvent(new CustomEvent('update-unread-messages'));
    } else {
      toast.error(response.data.message || 'Fehler beim Markieren');
    }
  } catch (error) {
    console.error('Fehler beim Markieren:', error);
    toast.error('Fehler beim Markieren der Nachrichten');
  } finally {
    loading.value = false;
  }
}

async function archiveAll() {
  if (!confirm('Möchten Sie wirklich alle Nachrichten archivieren?')) {
    return;
  }

  loading.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}messages.php`,
      { action: 'archive_all' },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success(`${response.data.count || 0} Nachricht(en) wurden archiviert`);
      await loadMessages();
      await loadStats();

      // Globalen Counter aktualisieren
      window.dispatchEvent(new CustomEvent('update-unread-messages'));
    } else {
      toast.error(response.data.message || 'Fehler beim Archivieren');
    }
  } catch (error) {
    console.error('Fehler beim Archivieren:', error);
    toast.error('Fehler beim Archivieren der Nachrichten');
  } finally {
    loading.value = false;
  }
}

async function sendMessage() {
  sending.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}messages.php`,
      newMessage.value,
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('Nachricht erfolgreich gesendet');
      closeNewMessageDialog();

      if (activeTab.value === 'sent') {
        loadMessages();
      }
      loadStats();
    } else {
      toast.error(response.data.message || 'Fehler beim Senden');
    }
  } catch (error) {
    console.error('Fehler beim Senden:', error);
    toast.error('Fehler beim Senden der Nachricht');
  } finally {
    sending.value = false;
  }
}

function closeNewMessageDialog() {
  showNewMessageDialog.value = false;
  newMessage.value = {
    recipient_id: null,
    subject: '',
    message_text: '',
    message_type: 'general',
    priority: 'normal'
  };
}

function handleWebSocketMessage(event) {
  const data = event.detail;

  if (data.type === 'new_message' || data.type === 'new_kv_confirmation' ||
      data.type === 'kv_completed' || data.type === 'kv_archived' ||
      data.type === 'kv_deleted' || data.type === 'kv_takeover') {
    // Neue Nachricht empfangen - aktualisieren
    loadMessages();
    loadStats();

    // Globalen Counter aktualisieren - IMMER bei neuen Nachrichten
    window.dispatchEvent(new CustomEvent('update-unread-messages'));

    // KEINE Toasts hier - werden zentral in App.vue angezeigt
    // MessageCenterView aktualisiert nur die Liste
  }
}

// Utility Functions
function getMessageColor(message) {
  if (message.message_type === 'kv_confirmation') return 'primary';
  if (message.priority === 'urgent') return 'error';
  if (message.priority === 'high') return 'warning';
  return 'grey';
}

function getMessageIcon(message) {
  if (message.message_type === 'kv_confirmation') return 'mdi-file-document-check';
  if (message.message_type === 'system') return 'mdi-information';
  if (message.message_type === 'warning') return 'mdi-alert';
  return 'mdi-email';
}

function getKvStatusColor(status) {
  if (status === 'reparieren') return 'success';
  if (status === 'warten_et') return 'warning';
  if (status === 'unrepariert') return 'error';
  return 'grey';
}

function getKvStatusText(status) {
  if (status === 'reparieren') return 'Reparieren';
  if (status === 'warten_et') return 'Warten auf Ersatzteil';
  if (status === 'unrepariert') return 'Unrepariert zurück';
  return 'Unbekannt';
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;

  // Weniger als 1 Minute
  if (diff < 60000) {
    return 'Gerade eben';
  }

  // Weniger als 1 Stunde
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `vor ${minutes} Min.`;
  }

  // Weniger als 24 Stunden
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `vor ${hours} Std.`;
  }

  // Standard-Format
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function truncateText(text, maxLength) {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
}
</script>

<style scoped>
.message-center-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.header-card,
.messages-card,
.message-detail-card,
.new-message-card {
  background: rgba(30, 30, 35, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.title-text {
  font-size: 1.5rem;
  font-weight: 600;
}

.header-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stats-chips {
  display: flex;
  gap: 8px;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.stats-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.message-list {
  max-height: 70vh;
  overflow-y: auto;
}

.message-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
  cursor: pointer;
}

.message-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.unread-message {
  background: rgba(76, 175, 80, 0.05);
  border-left: 3px solid #4CAF50;
}

.message-preview {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
}

.message-content {
  white-space: pre-wrap;
  line-height: 1.6;
  font-size: 1rem;
}

.empty-state {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.pulse-chip {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* ======== Mobile Optimierungen ======== */
@media (max-width: 959px) {
  .message-center-container {
    padding: 12px;
  }

  .header-card {
    margin-bottom: 12px !important;
  }

  .header-title {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 12px;
    padding: 12px !important;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .stats-row {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    padding-top: 0 !important;
  }

  .title-text {
    font-size: 1.25rem;
  }

  .message-list {
    max-height: calc(100vh - 300px);
  }

  .mobile-message-item {
    padding: 10px 8px !important;
  }

  .message-title-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start !important;
  }

  .message-subject {
    font-size: 0.95rem;
  }

  .message-chips {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  .message-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.8rem;
  }

  .message-sender,
  .message-time {
    display: flex;
    align-items: center;
  }

  .message-preview {
    font-size: 0.75rem;
  }

  .mobile-message-actions {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
  }

  .empty-state {
    min-height: 300px;
    padding: 16px !important;
  }

  .empty-state .v-icon {
    font-size: 60px !important;
  }

  .empty-state h3 {
    font-size: 1.1rem !important;
  }

  .empty-state p {
    font-size: 0.875rem !important;
  }
}

@media (max-width: 600px) {
  .message-center-container {
    padding: 8px;
  }

  .header-card,
  .messages-card {
    border-radius: 8px;
  }

  .stats-row .v-chip {
    font-size: 0.7rem;
  }

  .mobile-message-item {
    padding: 8px 6px !important;
  }

  .message-subject {
    font-size: 0.85rem;
  }

  .message-meta {
    font-size: 0.75rem;
  }

  .message-preview {
    font-size: 0.7rem;
  }
}
</style>
